/** @file IK chain rules at command time (§6.5): plain bones, parent→child, target outside the chain. */
import type { Doc, Id } from '@pose2d/format/types';
import { throwError } from '@pose2d/format/throwError';

type Problem = [path: string, reason: string, hint?: string] | null;

const CHILD_INHERIT = 'the child bone must use inherit "normal"';

/** The first animation key that gives `child` a non-normal inherit, as a hint naming it; null when none does. */
function animatedInherit(doc: Doc, child: Id): string | null {
  for (const anim of Object.values(doc.animations)) {
    const key = anim.bones[child]?.inherit?.find((k) => k.v !== 'normal');
    if (key) return `animation "${anim.name}" keys inherit "${key.v}" at frame ${key.f}; delete that key first (the two-bone solve needs "normal")`;
  }
  return null;
}

/** A generated socket bone in the chain, a second bone that is not a direct child, or a non-normal child (setup or key). */
function chainProblem(doc: Doc, bones: readonly Id[]): Problem {
  const socketAt = bones.findIndex((id) => doc.bones[id]?.kind !== undefined);
  if (socketAt >= 0) return [`bones.${socketAt}`, 'generated socket bones cannot be constrained; constrain the bone that carries the socket'];
  const [first, second] = bones;
  if (second === undefined) return null;
  const child = doc.bones[second];
  if (!child || child.parent !== first) return ['bones.1', 'the second bone must be a direct child of the first'];
  if (child.inherit !== 'normal') return ['bones.1', CHILD_INHERIT];
  const keyed = animatedInherit(doc, second);
  return keyed === null ? null : ['bones.1', CHILD_INHERIT, keyed];
}

/** A target inside the chain or below one of its bones (the solve would move its own target). */
function targetProblem(doc: Doc, bones: readonly Id[], target: Id): Problem {
  if (bones.includes(target)) return ['target', 'the target is one of the constrained bones'];
  for (let cur = doc.bones[target]?.parent ?? null; cur !== null; cur = doc.bones[cur]?.parent ?? null) {
    if (bones.includes(cur)) return ['target', 'the target must not be a descendant of a constrained bone'];
  }
  return null;
}

/** Throws IK_INVALID `{ ik, reason }` at `<at><field>` (e.g. `value.bones.1`) when the chain or target breaks §6.5. */
export function assertIkChain(doc: Doc, spec: { ik: string; bones: readonly Id[]; target: Id; at: string }): void {
  const problem = chainProblem(doc, spec.bones) ?? targetProblem(doc, spec.bones, spec.target);
  if (problem) throwError('IK_INVALID', { ik: spec.ik, reason: problem[1] }, { path: `${spec.at}${problem[0]}`, ...(problem[2] === undefined ? {} : { hint: problem[2] }) });
}
